<template>
  <div class="cfp">
    <div class="row cfp__row">
      <div class="col-md-4">
        <base-input label="First name contains" class="mb-0">
          <el-input v-model="firstName" placeholder="e.g. Jo" clearable @input="emitFilter" />
        </base-input>
      </div>
      <div class="col-md-4">
        <base-input label="Last name contains" class="mb-0">
          <el-input v-model="lastName" placeholder="e.g. Smith" clearable @input="emitFilter" />
        </base-input>
      </div>
      <div class="col-md-4">
        <base-input label="Email contains" class="mb-0">
          <el-input v-model="email" placeholder="e.g. @gmail." clearable @input="emitFilter" />
        </base-input>
      </div>
    </div>

    <div class="row cfp__row">
      <div class="col-md-4">
        <label>Played event</label>
        <base-input class="mb-0">
          <el-select
            v-model="gameIds"
            multiple
            filterable
            clearable
            placeholder="Any event"
            class="select-primary cfp__full"
            @change="emitFilter"
          >
            <el-option
              v-for="game in games"
              :key="game.id"
              :value="game.id"
              :label="game.internal_name || game.name"
              class="select-primary"
            />
          </el-select>
        </base-input>
      </div>
      <div class="col-md-4">
        <label>Last visited</label>
        <div class="cfp__pair">
          <base-input class="cfp__pair-mode mb-0">
            <el-select v-model="visitedOp" clearable placeholder="Any time" class="select-primary" @change="emitFilter">
              <el-option value="within_days" label="Within the last" class="select-primary" />
              <el-option value="more_than_days_ago" label="More than" class="select-primary" />
              <el-option value="never" label="Never visited" class="select-primary" />
            </el-select>
          </base-input>
          <base-input v-if="visitedOp && visitedOp !== 'never'" class="cfp__pair-days mb-0">
            <el-input-number v-model="visitedDays" :controls="false" :min="1" :precision="0" placeholder="90" @input="emitFilter" />
          </base-input>
          <label v-if="visitedOp === 'within_days'" class="cfp__pair-suffix">days</label>
          <label v-else-if="visitedOp === 'more_than_days_ago'" class="cfp__pair-suffix">days ago</label>
        </div>
      </div>
      <div class="col-md-4">
        <label>Lifetime spend</label>
        <div class="cfp__pair">
          <base-input class="cfp__pair-mode mb-0">
            <el-select v-model="spendOp" clearable placeholder="Any amount" class="select-primary" @change="emitFilter">
              <el-option value="gte" label="At least" class="select-primary" />
              <el-option value="lte" label="At most" class="select-primary" />
            </el-select>
          </base-input>
          <money-input
            v-if="spendOp"
            class="cfp__pair-value"
            :value="spendAmount"
            :min="0.01"
            @input="onSpendAmount"
          />
        </div>
      </div>
    </div>

    <div class="row cfp__row">
      <div v-if="groupOptions.length > 1" class="col-md-4">
        <label>Groups</label>
        <base-input class="mb-0">
          <el-select
            v-model="groupIds"
            multiple
            filterable
            clearable
            placeholder="Any group"
            class="select-primary cfp__full"
            @change="emitFilter"
          >
            <el-option
              v-for="g in groupOptions"
              :key="g.id"
              :value="g.id"
              :label="g.name"
              class="select-primary"
            />
          </el-select>
        </base-input>
        <info-hint v-if="scopedGroupNames.length" variant="primary" class="mt-2">
          Limited to your groups: {{ scopedGroupNames.join(', ') }}. Customers who have not visited these groups are not included.
        </info-hint>
      </div>
      <div :class="groupOptions.length > 1 ? 'col-md-8' : 'col-12'">
        <label>Tags</label>
        <div class="cfp__pair cfp__pair--tags">
          <base-input class="cfp__pair-mode mb-0">
            <el-select v-model="tagMode" class="select-primary" @change="emitFilter">
              <el-option value="any" label="Has any of" class="select-primary" />
              <el-option value="all" label="Has all of" class="select-primary" />
              <el-option value="none" label="Has none of" class="select-primary" />
            </el-select>
          </base-input>
          <base-input class="cfp__pair-value mb-0">
            <el-select
              v-model="tagNames"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="Type a tag and press enter"
              class="select-primary cfp__full"
              @change="emitFilter"
            >
              <el-option v-for="t in tagOptionNames" :key="t" :value="t" :label="t" class="select-primary" />
            </el-select>
          </base-input>
        </div>
      </div>
    </div>

    <div class="row cfp__row mb-0">
      <div class="col-md-8">
        <toggle-field
          v-if="mode !== 'ongoing'"
          :value="birthdayThisMonth"
          :active-value="true"
          :inactive-value="false"
          label="Birthday this month"
          hint="Includes every customer whose birthday falls in the current calendar month. Customers without a birthday on file are excluded."
          @input="onBirthdayThisMonth"
        />
      </div>
      <div class="col-md-4 text-right">
        <base-button v-if="hasActiveFilters" link type="primary" size="sm" class="m-0" @click="clearAll">
          Clear all filters
        </base-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import ToggleField from '@/components/PaymentPages/ToggleField.vue'

// Emits a filter_json object matching the backend audience schema:
// { first_name: {contains}, last_name, email, games: {ids}, groups: {ids},
//   tags: {mode, names}, last_visited: {op, days}, lifetime_spend: {op, amount},
//   birthday_month: {mode}, birthday_in_days: {days} }
// Empty object = the whole mailable customer base.
// mode 'ongoing' hides "Birthday this month": an ongoing campaign matches a
// customer on one day, not for a month. The daily condition itself
// (birthday_in_days) is owned by the campaign audience step, which merges it
// onto the filter this panel emits.
const props = defineProps({
  value: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  mode: { type: String as PropType<'one_time' | 'ongoing'>, default: 'one_time' },
  games: { type: Array as PropType<any[]>, default: () => [] },
  // Group ids the server clamped a restricted caller to; renders the hint
  scopedGroupIds: { type: Array as PropType<number[] | null>, default: null }
})

const emit = defineEmits<{ (e: 'input', value: Record<string, any>): void }>()

const initial = props.value || {}
const firstName = ref<string>(initial.first_name?.contains || '')
const lastName = ref<string>(initial.last_name?.contains || '')
const email = ref<string>(initial.email?.contains || '')
const gameIds = ref<number[]>(initial.games?.ids ? [...initial.games.ids] : [])
const groupIds = ref<number[]>(initial.groups?.ids ? [...initial.groups.ids] : [])
const tagMode = ref<string>(initial.tags?.mode || 'any')
const tagNames = ref<string[]>(initial.tags?.names ? [...initial.tags.names] : [])
const visitedOp = ref<string>(initial.last_visited?.op || '')
const visitedDays = ref<number | undefined>(initial.last_visited?.days)
const spendOp = ref<string>(initial.lifetime_spend?.op || '')
const spendAmount = ref<number | null>(initial.lifetime_spend?.amount ?? null)
const birthdayThisMonth = ref<boolean>(initial.birthday_month?.mode === 'current')

function buildFilter(): Record<string, any> {
  const filter: Record<string, any> = {}
  if (firstName.value.trim()) filter.first_name = { contains: firstName.value.trim() }
  if (lastName.value.trim()) filter.last_name = { contains: lastName.value.trim() }
  if (email.value.trim()) filter.email = { contains: email.value.trim() }
  if (gameIds.value.length) filter.games = { ids: [...gameIds.value] }
  if (groupIds.value.length) filter.groups = { ids: [...groupIds.value] }
  if (tagNames.value.length) filter.tags = { mode: tagMode.value, names: [...tagNames.value] }
  if (visitedOp.value === 'never') {
    filter.last_visited = { op: 'never' }
  } else if (visitedOp.value && visitedDays.value && visitedDays.value > 0) {
    filter.last_visited = { op: visitedOp.value, days: Math.round(visitedDays.value) }
  }
  if (spendOp.value && spendAmount.value && spendAmount.value > 0) {
    // Threshold input, raw number by design; no cents conversion happens here
    filter.lifetime_spend = { op: spendOp.value, amount: spendAmount.value }
  }
  if (props.mode !== 'ongoing' && birthdayThisMonth.value) filter.birthday_month = { mode: 'current' }
  return filter
}

const hasActiveFilters = computed(() => Object.keys(buildFilter()).length > 0)

// Tag options come from the group's tag library; allow-create keeps legacy
// names (present in saved filters but absent from the library) typeable.
// Selected names not in the library still render as options so they show
// as active chips.
const libraryTagNames = ref<string[]>([])
const tagOptionNames = computed(() => {
  const names = new Set<string>(libraryTagNames.value)
  tagNames.value.forEach((n) => names.add(n))
  return [...names]
})

async function loadLibraryTags() {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const token = user.signInUserSession.idToken.jwtToken
    const companyId = localStorage.getItem('companyId')
    const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
    const response = await axios.get(
      `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/tags`,
      { headers: { Authorization: token } }
    )
    libraryTagNames.value = (response.data || []).map((t: any) => t.name)
  } catch (e) {
    // Library unavailable: the select still works via allow-create
  }
}

// Group options come from the caller's own groups (the endpoint only returns
// groups the caller is linked to), so the picker can never offer a group the
// caller cannot view. Hidden for single-group callers.
const groupOptions = ref<{ id: number; name: string }[]>([])
async function loadGroupOptions() {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const token = user.signInUserSession.idToken.jwtToken
    const companyId = localStorage.getItem('companyId')
    const response = await axios.get(
      `${API_LOCATION}companies/${companyId}/groups`,
      { headers: { Authorization: token } }
    )
    groupOptions.value = (response.data || []).map((g: any) => ({ id: Number(g.id), name: g.name }))
  } catch (e) {
    // Options unavailable: the field stays hidden
  }
}
const scopedGroupNames = computed(() => {
  const ids = new Set((props.scopedGroupIds || []).map(Number))
  return groupOptions.value.filter((g) => ids.has(g.id)).map((g) => g.name)
})

// A server clamp can add groups to the filter after the first preview; keep
// the chips in step without remounting the panel (which would drop typed text).
watch(() => props.value?.groups?.ids, (ids) => {
  const next = Array.isArray(ids) ? ids.map(Number) : []
  const same = next.length === groupIds.value.length && next.every((id, i) => id === groupIds.value[i])
  if (!same) groupIds.value = next
})

onMounted(() => {
  loadLibraryTags()
  loadGroupOptions()
})

function emitFilter() {
  emit('input', buildFilter())
}

function onBirthdayThisMonth(value: boolean) {
  birthdayThisMonth.value = value
  emitFilter()
}

// Switching the campaign type drops the keys the other type does not use.
watch(() => props.mode, () => {
  emitFilter()
})

function onSpendAmount(value: number | null) {
  spendAmount.value = value
  emitFilter()
}

function clearAll() {
  firstName.value = ''
  lastName.value = ''
  email.value = ''
  gameIds.value = []
  groupIds.value = []
  tagNames.value = []
  visitedOp.value = ''
  visitedDays.value = undefined
  spendOp.value = ''
  spendAmount.value = null
  birthdayThisMonth.value = false
  emitFilter()
}
</script>

<script lang="ts">
export default { name: 'customer-filter-panel' }
</script>

<style lang="scss" scoped>
// Layout only. Labels, inputs, selects, toggle, hint, and button all come
// from theme/global primitives (base-input, select-primary, toggle-field,
// money-input, base-button).
.cfp {
  &__row {
    margin-bottom: 0.75rem;

    ::v-deep .form-group {
      margin-bottom: 0;
    }
  }

  &__pair {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__pair-mode {
    flex: 1;
    min-width: 150px;
  }

  &__pair-value {
    flex: 1;
    min-width: 0;
  }

  // Tag picker holds many pills; give it twice the mode select's share
  &__pair--tags {
    .cfp__pair-mode {
      flex: 1;
      max-width: 200px;
    }
    .cfp__pair-value {
      flex: 3;
    }

    // Phones: mode select on its own line, tag picker full width below
    @media (max-width: 767px) {
      .cfp__pair-mode {
        flex: 0 0 100%;
        max-width: none;
      }
      .cfp__pair-value {
        flex: 0 0 100%;
      }
    }
  }

  &__pair-days {
    width: 90px;
    flex-shrink: 0;

    ::v-deep .el-input-number {
      width: 100%;
    }

    ::v-deep .el-input-number .el-input__inner {
      text-align: left;
    }
  }

  &__pair-suffix {
    margin: 0;
    white-space: nowrap;
  }

  &__full {
    width: 100%;
  }
}

</style>
